import { instance } from '@app/api/axios';
import { useShowToaster } from '@shared/ui/messages/useToaster';
import { useRequestError } from '@app/api/useRequestError';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchUser, updateUser } from '@app/store/slices/userSlice';
import { useLoginTwofaDialog } from '@utils/dialogs-hooks';
import { startPreloader } from '@app/store/slices/preloaderSlice';
import {
  clearTempToken,
  setAuthTokens,
  setTempToken,
  clearSession,
} from '@app/api/tokenService';
import { persistor } from '@app/store';
import { getLang, withAuthLang } from '@app/i18n';
import { clearReferrer } from '@utils/referrer';

// do not delete api: ''

export function useAuth() {
  const showToaster = useShowToaster();
  const handleRequestError = useRequestError();
  const navigate = useNavigate();
  const { openLoginTwofaDialog } = useLoginTwofaDialog();
  const dispatch = useDispatch();

  const startSession = (data) => {
    setAuthTokens(data);
    dispatch(startPreloader());
    dispatch(fetchUser());
    navigate('/');
  };

  const login = async (data) => {
    try {
      clearTempToken();
      const res = await instance.post('/login?lang=' + getLang(), {
        ...data,
        secure_mode: false,
        api: '',
      });

      if (res?.data?.need_2fa) {
        setTempToken(res.data.temp_token);
        openLoginTwofaDialog();
      } else {
        startSession(res.data);
      }
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const loginWithTwoFa = async (data) => {
    try {
      const res = await instance.post('/login/google2fa?lang=' + getLang(), {
        ...data,
        api: '',
      });
      clearTempToken();
      startSession(res.data);
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const signup = async (data) => {
    try {
      const res = await instance.post('/registration?lang=' + getLang(), {
        ...data,
        api: '',
      });
      if (res.status === 201) {
        try {
          window.gtag?.('event', 'sign_up', { method: 'email' });
        } catch {
          // аналитика не должна ломать регистрацию
        }
      }
      clearReferrer();
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const validateActivationToken = async (data) => {
    try {
      const res = await instance.post('/verify-token', { ...data, api: '' });
      return res?.data?.valid;
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
      throw err;
    }
  };

  const activateUser = async (data, token) => {
    try {
      const res = await instance.post('/setpassword', {
        ...data,
        token,
        api: '',
      });
      showToaster('success', res?.data?.message);
      navigate(withAuthLang('/login'));
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const resendActivation = async (data) => {
    try {
      const res = await instance.post(
        '/email/resend-verification-notification?lang=' + getLang(),
        data,
      );
      showToaster('success', res?.data?.message);
      return true;
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
      return false;
    }
  };

  const forgotPassword = async (data) => {
    try {
      await instance.post('/password/forgot?lang=' + getLang(), {
        ...data,
        api: '',
      });
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const validateResetPasswordToken = async (data) => {
    try {
      const res = await instance.post('/check-reset-token', {
        ...data,
        api: '',
      });
      return res?.data?.valid;
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
      throw err;
    }
  };

  const resetPassword = async (data, token, email) => {
    try {
      const res = await instance.post('/password/reset?lang=' + getLang(), {
        ...data,
        token,
        email,
        api: '',
      });
      showToaster('success', res?.data?.message);
      navigate(withAuthLang('/login'));
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  //todo: добавлен новый параметр device all, проверить
  const logoutUser = async () => {
    try {
      await instance.post('/logout');
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
    } finally {
      clearSession();
      dispatch({ type: 'logout' });
      persistor.persist();
      navigate('/login');
    }
  };

  const generateTwofa = async () => {
    try {
      const res = await instance.get('/google2fa/generate');
      return res?.data;
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
    }
  };

  const enableTwofa = async (data, qrData) => {
    try {
      const res = await instance.post('/google2fa/enable', {
        ...data,
        google2fa_secret: qrData?.google2fa_secret,
      });
      dispatch(updateUser({ two_fa: true }));
      showToaster('success', res?.data?.message);
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const disableTwofa = async (data) => {
    try {
      const res = await instance.post('/google2fa/disable', data);
      dispatch(updateUser({ two_fa: false }));
      showToaster('success', res?.data?.message);
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  return {
    login,
    loginWithTwoFa,
    signup,
    validateActivationToken,
    activateUser,
    resendActivation,
    forgotPassword,
    validateResetPasswordToken,
    resetPassword,
    logoutUser,
    generateTwofa,
    enableTwofa,
    disableTwofa,
  };
}
